import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { GenabilityService } from "~/api/services/genability/genability.service"
import type {
  IntelligentBaselineRequestDto,
  IntelligentBaselineUsageRequestDto,
  AnnualUsageRequestDto,
  IntelligentBaselineResponseDto,
} from "~/api/services/genability/genability.types"

const QUERY_KEYS = {
  intelligentBaseline: (accountId: string, inputHash?: string) => [
    `genability`,
    `intelligent-baseline`,
    accountId,
    ...(inputHash ? [inputHash] : []),
  ],
}

export const useEstimateFromMonthlyCosts = (): UseMutationResult<
  IntelligentBaselineResponseDto,
  Error,
  IntelligentBaselineRequestDto
> => {
  const queryClient = useQueryClient()

  return useMutation<
    IntelligentBaselineResponseDto,
    Error,
    IntelligentBaselineRequestDto
  >({
    mutationFn: GenabilityService.estimateFromMonthlyCosts,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        QUERY_KEYS.intelligentBaseline(variables.accountId),
        data,
      )
    },
  })
}

export const useEstimateFromMonthlyUsage = (): UseMutationResult<
  IntelligentBaselineResponseDto,
  Error,
  IntelligentBaselineUsageRequestDto
> => {
  const queryClient = useQueryClient()

  return useMutation<
    IntelligentBaselineResponseDto,
    Error,
    IntelligentBaselineUsageRequestDto
  >({
    mutationFn: GenabilityService.estimateFromMonthlyUsage,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        QUERY_KEYS.intelligentBaseline(variables.accountId),
        data,
      )
    },
  })
}

export const useEstimateFromAnnualUsage = (): UseMutationResult<
  IntelligentBaselineResponseDto,
  Error,
  AnnualUsageRequestDto
> => {
  const queryClient = useQueryClient()

  return useMutation<
    IntelligentBaselineResponseDto,
    Error,
    AnnualUsageRequestDto
  >({
    mutationFn: GenabilityService.estimateFromAnnualUsage,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        QUERY_KEYS.intelligentBaseline(variables.accountId),
        data,
      )
    },
  })
}

export const useIntelligentBaselineData = (
  accountId: string | undefined,
): UseQueryResult<IntelligentBaselineResponseDto | null> => {
  return useQuery<IntelligentBaselineResponseDto | null>({
    queryKey: QUERY_KEYS.intelligentBaseline(accountId ?? ``),
    queryFn: () => null,
    enabled: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
